/**
 * Shared value formatters for charts, tables, and widget renderers.
 *
 * Use these instead of inline formatting functions so that every widget
 * displays numbers, dates, and percentages consistently.
 */

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

/** Format with K/M/B/T abbreviation: 1234 → "1.2K", 1234567 → "1.2M" */
export function formatCompact(value: number | null | undefined): string {
  if (value == null) return '--';
  const abs = Math.abs(value);
  if (abs >= 1e12) return `${(value / 1e12).toFixed(1)}T`;
  if (abs >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(1);
}

/** Format integer with locale grouping: 1234567 → "1,234,567" */
export function formatInteger(value: number | null | undefined): string {
  if (value == null) return '--';
  return Math.round(value).toLocaleString();
}

/** Format float with fixed decimals: 3.14159 → "3.14" */
export function formatFloat(value: number | null | undefined, decimals = 2): string {
  if (value == null) return '--';
  return value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// ---------------------------------------------------------------------------
// Prices & currency
// ---------------------------------------------------------------------------

/** Format as price: 187.44 → "$187.44", 2850000000000 → "$2.9T" */
export function formatPrice(value: number | null | undefined, decimals = 2): string {
  if (value == null) return '--';
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs >= 1e12) return `${sign}$${(abs / 1e12).toFixed(1)}T`;
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
  return `${sign}$${abs.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

// ---------------------------------------------------------------------------
// Returns & percentages
// ---------------------------------------------------------------------------

/** Format as percentage: 0.1234 → "+12.3%", -0.05 → "-5.0%" */
export function formatReturn(value: number | null | undefined, decimals = 1): string {
  if (value == null) return '--';
  const pct = value * 100;
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(decimals)}%`;
}

/** Format as percentage (already in pct form): 12.34 → "12.3%" */
export function formatPercent(value: number | null | undefined, decimals = 1): string {
  if (value == null) return '--';
  return `${value.toFixed(decimals)}%`;
}

// ---------------------------------------------------------------------------
// Ratios & multiples
// ---------------------------------------------------------------------------

/** Format as multiple: 28.5 → "28.5x" */
export function formatMultiple(value: number | null | undefined, decimals = 1): string {
  if (value == null) return '--';
  return `${value.toFixed(decimals)}x`;
}

// ---------------------------------------------------------------------------
// Volume (shares / units)
// ---------------------------------------------------------------------------

/** Format volume with K/M abbreviation: 52341200 → "52.3M" */
export function formatVolume(value: number | null | undefined): string {
  if (value == null) return '--';
  if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(0)}K`;
  return value.toLocaleString();
}

// ---------------------------------------------------------------------------
// Dates & times
// ---------------------------------------------------------------------------

/** Format ISO date string: "2025-03-14" → "Mar 14, 2025" */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '--';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Format ISO date string short: "2025-03-14" → "Mar 14" */
export function formatDateShort(value: string | null | undefined): string {
  if (!value) return '--';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Format ISO datetime: "2025-03-14T09:30:00Z" → "Mar 14, 2025 9:30 AM" */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '--';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// AG-Charts axis formatter helpers
// ---------------------------------------------------------------------------

/** Params AG-Charts passes to an axis label formatter. X (category/time) axes
 *  carry string values, Y (number) axes carry numbers — hence number | string. */
export interface AxisLabelFormatterParams {
  value: number | string;
}

/** Signature of an AG-Charts axis label formatter. */
export type AxisLabelFormatter = (params: AxisLabelFormatterParams) => string;

/** Params AG-Charts passes to a series tooltip renderer. `datum` is the hovered
 *  data row; other fields (xKey, yKey, ...) vary by series type. */
export interface ChartTooltipParams {
  datum?: Record<string, unknown>;
  [key: string]: unknown;
}

/** ag-charts v13 tooltip renderer result. NOTE: the legacy {title, content}
 *  shape is silently dropped by v13 — rows must go in `data`, and in shared
 *  mode AG groups every series' rows under one heading with color dots. */
export interface ChartTooltipResult {
  heading?: string;
  title?: string;
  data?: Array<{ label: string; value: string }>;
}

/** For use as an AG-Charts axis label formatter — compact numbers */
export function axisCompact(params: AxisLabelFormatterParams): string {
  return formatCompact(Number(params.value));
}

/** For use as an AG-Charts axis label formatter — prices */
export function axisPrice(params: AxisLabelFormatterParams): string {
  return formatPrice(Number(params.value), 0);
}

/** For use as an AG-Charts axis label formatter — percentages */
export function axisPercent(params: AxisLabelFormatterParams): string {
  return formatPercent(Number(params.value), 0);
}

// ---------------------------------------------------------------------------
// Time-aware axis formatting
// ---------------------------------------------------------------------------

type TimeScale = 'years' | 'months' | 'days' | 'hours' | 'minutes';

/**
 * Detect the appropriate time scale from an array of ISO date/datetime strings.
 * Looks at the span and interval between data points.
 */
export function detectTimeScale(dates: string[]): TimeScale {
  if (dates.length < 2) return 'days';
  const timestamps = dates.map((d) => new Date(d).getTime()).filter((t) => !isNaN(t));
  if (timestamps.length < 2) return 'days';

  // Sort timestamps to handle unsorted data
  const sorted = [...timestamps].sort((a, b) => a - b);
  const span = sorted[sorted.length - 1] - sorted[0];
  if (span === 0) return 'days';
  const avgInterval = span / (sorted.length - 1);

  const MINUTE = 60_000;
  const HOUR = 3_600_000;
  const DAY = 86_400_000;
  const MONTH = 30 * DAY;

  if (avgInterval < 5 * MINUTE) return 'minutes';
  if (avgInterval < 2 * HOUR) return 'hours';
  if (avgInterval < 20 * DAY) return 'days';
  if (avgInterval < 6 * MONTH) return 'months';
  return 'years';
}

/** Format a date string according to the detected time scale */
export function formatByScale(value: string, scale: TimeScale): string {
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;

  // 2-digit year suffix ("'25") so the year is always visible on date-scale
  // axes — including short (days) ranges that previously showed only "Mar 14".
  const yy = `'${String(d.getFullYear()).slice(-2)}`;

  switch (scale) {
    case 'minutes':
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    case 'hours':
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    case 'days':
      return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${yy}`;
    case 'months':
      return `${d.toLocaleDateString('en-US', { month: 'short' })} ${yy}`;
    case 'years':
      return d.toLocaleDateString('en-US', { year: 'numeric' });
  }
}

/**
 * Pick explicit axis tick dates roughly every `stepDays` from an ascending
 * date array: the first date, then the first date ≥ previous tick + step.
 * Returned values are members of the input (a category axis only honors
 * tick values that exactly match its categories).
 */
export function buildQuarterlyTicks(dates: string[], stepDays = 90): string[] {
  if (dates.length === 0) return [];
  const ticks: string[] = [dates[0]];
  let lastTick = new Date(dates[0]).getTime();
  const step = stepDays * 86_400_000;
  for (const d of dates) {
    const t = new Date(d).getTime();
    if (!isNaN(t) && t >= lastTick + step) {
      ticks.push(d);
      lastTick = t;
    }
  }
  return ticks;
}

/** Month + year axis label ("Mar '25") — pair with explicit tick values;
 *  unlike buildTimeAxisFormatter it never blanks labels by index. */
export function axisMonthYear(params: AxisLabelFormatterParams): string {
  return formatByScale(String(params.value), 'months');
}

/**
 * Build an AG-Charts category axis label formatter that auto-detects
 * the time scale from the data and formats accordingly.
 * Also thins labels to avoid overlap (shows ~maxLabels evenly spaced).
 */
export function buildTimeAxisFormatter(dates: string[], maxLabels = 8) {
  const scale = detectTimeScale(dates);
  const interval = Math.max(1, Math.ceil(dates.length / maxLabels));

  return (params: AxisLabelFormatterParams) => {
    const value = String(params.value);
    const idx = dates.indexOf(value);
    if (idx === -1) return formatByScale(value, scale);
    // Show every Nth label + always the last
    if (idx % interval !== 0 && idx !== dates.length - 1) return '';
    return formatByScale(value, scale);
  };
}
